import { FC, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { AppBar, Box, IconButton, Toolbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { TextFieldSearch } from '@/shared/ui/components/textfield/TextFieldSearch';

import ALogo from '@/shared/ui/icons/ALogoGradient.svg';

import { useGlobalSearch } from '@/shared/lib/useGlobalSearch';

import { TRole } from '@/shared/types/role';

import {
  ROUTES,
  SEARCH_PLACEHOLDER_ACTORS,
  SEARCH_PLACEHOLDER_CASTING_OR_PROJECT
} from '@/shared/constants';

import { APPBAR_HEIGHT } from '@/shared/ui/constants';

import { useGetMenuCounters } from '@/entities/menu-counters';
import { NotificationsLayout } from '@/entities/notifications';

interface IProps {
  onMenuClick: () => void;
  role: TRole;
}

/**
 * Верхняя мобильная панель: burger + logo + поиск (иконка → раскрытие на весь ряд) + уведомления.
 * @param root0 Пропсы
 * @param root0.onMenuClick Открытие бокового меню
 * @param root0.role Роль пользователя
 */
export const MobileTopBar: FC<IProps> = ({ onMenuClick, role }) => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { searchValue, setSearchValue, onSubmit } = useGlobalSearch(role);
  const { notificationsUnviewedCount, isLoading } = useGetMenuCounters();

  const searchPlaceholder =
    role === 'director'
      ? SEARCH_PLACEHOLDER_ACTORS
      : SEARCH_PLACEHOLDER_CASTING_OR_PROJECT;

  /**
   * Отправляет глобальный поиск и сворачивает поле обратно в иконку
   */
  const handleSearchSubmit = () => {
    onSubmit();
    setIsSearchOpen(false);
  };

  return (
    <AppBar
      position='fixed'
      sx={{
        height: APPBAR_HEIGHT,
        justifyContent: 'center',
        background: `linear-gradient(135deg, #242269 0%, #121533 25.5%, #121533 75%, #112d7a 100%)`,
        zIndex: 1100
      }}
    >
      <Toolbar
        variant='dense'
        sx={{
          gap: 2,
          minHeight: APPBAR_HEIGHT,
          minWidth: 0,
          // Икон-кнопки панели по умолчанию 40×40 (иконка 24 + padding 8) —
          // ниже тач-минимума 44. Поднимаем через minWidth/minHeight, а не
          // height: панель живёт только на мобильной оболочке (< lg), в 56px
          // высоты 44 умещаются, а геометрия иконок не меняется.
          '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 }
        }}
      >
        {isSearchOpen ? (
          <>
            <IconButton
              aria-label='Закрыть поиск'
              onClick={() => setIsSearchOpen(false)}
            >
              <ArrowBackIcon sx={{ color: 'common.white' }} />
            </IconButton>
            <TextFieldSearch
              value={searchValue}
              setValue={setSearchValue}
              onSubmit={handleSearchSubmit}
              placeholder={searchPlaceholder}
              sx={{ flexGrow: 1, minWidth: 0 }}
            />
          </>
        ) : (
          <>
            <IconButton aria-label='Открыть меню' onClick={onMenuClick}>
              <MenuIcon sx={{ color: 'common.white' }} />
            </IconButton>
            <IconButton
              aria-label='На главную'
              onClick={() => navigate(ROUTES.root)}
              sx={{ flexShrink: 0 }}
            >
              <ALogo style={{ width: 28, height: 41 }} />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              aria-label='Открыть поиск'
              onClick={() => setIsSearchOpen(true)}
            >
              <SearchIcon sx={{ color: 'common.white' }} />
            </IconButton>
            <NotificationsLayout
              compact
              type='light'
              count={notificationsUnviewedCount}
              isLoading={isLoading}
            />
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};
